import React, { useState, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Slider } from '../components/ui/slider';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import SEO from '../components/SEO';
import {
  DollarSign,
  Users,
  Server,
  HardDrive,
  Globe,
  Bot,
  Search,
  Shield,
  Headphones,
  Eye,
  Heart,
  Code,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Building2,
  Calculator,
  AlertCircle,
  Zap,
  Scale,
  Crown,
  PiggyBank,
  FileText,
  Percent,
  Lightbulb,
  Info,
  Lock,
  Printer,
  Download
} from 'lucide-react';

const BusinessFinancials = () => {
  const { user, loading } = useAuth();
  
  // Wait for auth state to load
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // Access control: VBB personnel only
  const isVBBPersonnel = user?.is_operator_admin;
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (!isVBBPersonnel) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-red-50 to-white">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Lock className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Access Restricted</h2>
            <p className="text-muted-foreground">
              This document is only accessible to VBB management personnel.
            </p>
            <Button className="mt-4" onClick={() => window.history.back()}>
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ==================== STATE ====================
  // Note: Hooks must be called before conditional returns, but we need them after auth check
  // So we'll render a wrapper component pattern
  
  return <BusinessFinancialsContent />;
};

// Separate content component to handle hooks properly
const BusinessFinancialsContent = () => {
  // ==================== STATE ====================
  const [selectedScale, setSelectedScale] = useState('100K');
  const [utilityMargin, setUtilityMargin] = useState(15); // 12-18% range
  const [costBasis, setCostBasis] = useState('full'); // 'direct' or 'full'
  const [customClubRevenue, setCustomClubRevenue] = useState(10000);
  const [foundingClubFee, setFoundingClubFee] = useState(1); // 1-3% range
  const [printMode, setPrintMode] = useState(false);

  // Print mode styles
  const printStyles = `
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .no-print { display: none !important; }
      .print-break { page-break-before: always; }
      .bg-gradient-to-b, .bg-gradient-to-r { background: white !important; }
      .bg-slate-800, .bg-slate-800\\/50 { background: #f8fafc !important; }
      .text-white { color: #1e293b !important; }
      .text-slate-300, .text-slate-400 { color: #475569 !important; }
      .border-slate-700 { border-color: #e2e8f0 !important; }
      nav, footer, .cookie-banner { display: none !important; }
    }
  `;

  // Inject print styles
  React.useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.innerHTML = printStyles;
    document.head.appendChild(styleEl);
    return () => document.head.removeChild(styleEl);
  }, [printStyles]);

  // ==================== COST DATA ====================
  const scaleData = {
    '10K': {
      users: '10,000',
      label: 'Early Stage',
      color: 'bg-green-500',
      infra: { compute: 50, database: 50, storage: 20, cdn: 20, ai: 10, search: 0, other: 20, total: 170 },
      labor: { support: 0, moderation: 0, community: 0, devops: 0, trustSafety: 0, total: 0 },
      staff: '1 (Founder)',
      perUser: 0.005
    },
    '100K': {
      users: '100,000',
      label: 'Growth',
      color: 'bg-blue-500',
      infra: { compute: 50, database: 50, storage: 30, cdn: 20, ai: 20, search: 0, other: 30, total: 200 },
      labor: { support: 2000, moderation: 1200, community: 2500, devops: 4000, trustSafety: 0, total: 9700 },
      staff: '3-4',
      perUser: 0.10
    },
    '500K': {
      users: '500,000',
      label: 'Scale',
      color: 'bg-purple-500',
      infra: { compute: 150, database: 200, storage: 150, cdn: 200, ai: 150, search: 100, other: 50, total: 1000 },
      labor: { support: 12000, moderation: 8000, community: 7500, devops: 10000, trustSafety: 6500, total: 44000 },
      staff: '8-12',
      perUser: 0.09
    },
    '1M': {
      users: '1,000,000',
      label: 'Mainstream',
      color: 'bg-amber-500',
      infra: { compute: 300, database: 500, storage: 300, cdn: 200, ai: 300, search: 200, other: 200, total: 2000 },
      labor: { support: 35000, moderation: 20000, community: 14000, devops: 23000, trustSafety: 16000, total: 108000 },
      staff: '18-25',
      perUser: 0.11
    },
    '10M': {
      users: '10,000,000',
      label: 'Enterprise',
      color: 'bg-red-500',
      infra: { compute: 2000, database: 5000, storage: 3000, cdn: 500, ai: 3000, search: 1000, other: 1500, total: 16000 },
      labor: { support: 300000, moderation: 185000, community: 70000, devops: 75000, trustSafety: 65000, total: 695000 },
      staff: '130-180',
      perUser: 0.07
    }
  };

  // ==================== AD REVENUE DATA ====================
  const adRevenueProjections = [
    { mau: 1000, pageViews: 15000, adImpressions: 12000, cpm: 2.5, rpm: 8.0, monthlyRevenue: 120, annualRevenue: 1440, status: 'beta' },
    { mau: 5000, pageViews: 75000, adImpressions: 60000, cpm: 3.0, rpm: 10.0, monthlyRevenue: 750, annualRevenue: 9000, status: 'growth' },
    { mau: 10000, pageViews: 150000, adImpressions: 120000, cpm: 3.5, rpm: 12.0, monthlyRevenue: 1800, annualRevenue: 21600, status: 'growth' },
    { mau: 50000, pageViews: 750000, adImpressions: 600000, cpm: 4.0, rpm: 15.0, monthlyRevenue: 11250, annualRevenue: 135000, status: 'scale' },
    { mau: 100000, pageViews: 1500000, adImpressions: 1200000, cpm: 5.0, rpm: 18.0, monthlyRevenue: 27000, annualRevenue: 324000, status: 'scale' }
  ];

  const adAssumptions = [
    { label: 'Avg Page Views per MAU', value: '15 pages/month' },
    { label: 'Ad Fill Rate', value: '80%' },
    { label: 'Ads per Page', value: '2-3 ads' },
    { label: 'Ad Network', value: 'Google AdSense' },
    { label: 'Audience Niche', value: 'Pet/Dog owners (premium CPM)' },
    { label: 'Guest vs Member Split', value: '70% guest (ads) / 30% members (no ads)' }
  ];

  // ==================== UTILITY MODEL CALCULATIONS ====================
  const current = scaleData[selectedScale];
  const directCosts = current.infra.total;
  const fullLoadedCosts = current.infra.total + current.labor.total;
  const baseCost = costBasis === 'direct' ? directCosts : fullLoadedCosts;
  
  // Utility model: Cost + Margin
  const utilityFee = useMemo(() => {
    return baseCost * (1 + utilityMargin / 100);
  }, [baseCost, utilityMargin]);

  // Custom club calculator
  const customClubUtilityFee = useMemo(() => {
    // Proportional cost allocation based on club size relative to platform
    const platformUsers = parseInt(selectedScale.replace('K', '000').replace('M', '000000'));
    const clubShareOfPlatform = 0.1; // Assume club is 10% of platform for estimation
    const clubProportionalCost = baseCost * clubShareOfPlatform;
    return clubProportionalCost * (1 + utilityMargin / 100);
  }, [baseCost, utilityMargin, selectedScale]);

  // Founding club remuneration calculation
  const foundingClubRemuneration = useMemo(() => {
    return customClubRevenue * (foundingClubFee / 100);
  }, [customClubRevenue, foundingClubFee]);

  // ==================== COMPARISON DATA ====================
  const modelComparison = useMemo(() => {
    const platformRevenue = customClubRevenue * 10; // Assume 10 clubs
    
    return [
      {
        model: 'Current (45% Revenue Share)',
        vbbShare: customClubRevenue * 0.45,
        clubRetains: customClubRevenue * 0.55,
        pros: ['Simple to understand', 'Aligns VBB with club success'],
        cons: ['Opaque cost structure', 'High perceived take rate', 'Clubs may feel exploited as they grow'],
        color: 'bg-amber-500'
      },
      {
        model: `Utility Model (Cost + ${utilityMargin}%)`,
        vbbShare: customClubUtilityFee,
        clubRetains: customClubRevenue - customClubUtilityFee,
        pros: ['Transparent costs', 'Regulated profit margin', 'Builds trust', 'Auditable'],
        cons: ['More complex accounting', 'VBB profit capped', 'Requires cost tracking'],
        color: 'bg-blue-500'
      },
      {
        model: 'Hybrid (Utility + IP Royalty)',
        vbbShare: customClubUtilityFee + (customClubRevenue * 0.03),
        clubRetains: customClubRevenue - customClubUtilityFee - (customClubRevenue * 0.03),
        pros: ['Transparent operations cost', 'Recognizes IP value', 'Future-proof'],
        cons: ['Two fee components', 'Perpetual royalty may concern some'],
        color: 'bg-purple-500'
      }
    ];
  }, [customClubRevenue, customClubUtilityFee, utilityMargin]);

  // ==================== HELPERS ====================
  const formatCurrency = (amount) => {
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
    return `$${amount.toFixed(0)}`;
  };

  const getStatusBadge = (status) => {
    const configs = {
      beta: { label: 'Beta', className: 'bg-blue-500' },
      growth: { label: 'Growth', className: 'bg-green-500' },
      scale: { label: 'Scale', className: 'bg-purple-500' }
    };
    const config = configs[status] || configs.beta;
    return <Badge className={`${config.className} text-white text-xs`}>{config.label}</Badge>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="business-financials-page">
      <SEO 
        title="Business Financials - Revenue & Cost Analysis"
        description="Financial projections, cost analysis, and business models for the AADC platform."
        keywords="business plan, revenue projections, cost analysis, utility model"
      />

      {/* Hero Section */}
      <section className="py-12 px-4 bg-gradient-to-r from-slate-800 to-slate-900 text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center">
              <PiggyBank className="w-7 h-7" />
            </div>
            <div>
              <Badge className="mb-2 bg-amber-500/20 text-amber-300">Investor Document</Badge>
              <h1 className="text-3xl md:text-4xl font-bold">Business Financials Review</h1>
            </div>
          </div>
          <p className="text-slate-300 text-lg max-w-2xl">
            Comprehensive analysis of platform costs, revenue projections, and business model options. 
            This document is for internal review and investor presentations.
          </p>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="px-4 py-8">
        <div className="container mx-auto max-w-6xl">
          <Tabs defaultValue="utility-model" className="space-y-6">
            <TabsList className="grid w-full grid-cols-4 lg:grid-cols-4">
              <TabsTrigger value="utility-model" className="gap-2">
                <Scale className="w-4 h-4 hidden sm:inline" />
                Utility Model
              </TabsTrigger>
              <TabsTrigger value="costs" className="gap-2">
                <Server className="w-4 h-4 hidden sm:inline" />
                Infrastructure
              </TabsTrigger>
              <TabsTrigger value="revenue" className="gap-2">
                <TrendingUp className="w-4 h-4 hidden sm:inline" />
                Ad Revenue
              </TabsTrigger>
              <TabsTrigger value="founding" className="gap-2">
                <Crown className="w-4 h-4 hidden sm:inline" />
                Founding Club
              </TabsTrigger>
            </TabsList>

            {/* ==================== UTILITY MODEL TAB ==================== */}
            <TabsContent value="utility-model" className="space-y-6">
              {/* Concept Explainer */}
              <Card className="border-blue-200 bg-blue-50/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-blue-800">
                    <Lightbulb className="w-5 h-5" />
                    The Utility Model Concept
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-blue-900">
                  <p className="mb-4">
                    <strong>Regulated utilities</strong> (electric, gas, water) operate under a simple, transparent framework:
                  </p>
                  <ul className="space-y-2 mb-4">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Cost Recovery:</strong> All operational costs are documented and recoverable</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Regulated Margin:</strong> Profit is guaranteed but capped (typically 8-15% return)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Transparency:</strong> All operations subject to review and audit</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Trust:</strong> Both parties know exactly what they're paying for</span>
                    </li>
                  </ul>
                  <p className="text-sm text-blue-700 bg-blue-100 p-3 rounded-lg">
                    <strong>For VBB:</strong> Instead of "45% of your revenue," we say "Cost of serving your club + {utilityMargin}% margin."
                    This builds trust and eliminates the perception of exploitation.
                  </p>
                </CardContent>
              </Card>

              {/* Utility Calculator */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calculator className="w-5 h-5" />
                    Utility Model Calculator
                  </CardTitle>
                  <CardDescription>
                    Adjust parameters to see how different scenarios affect club costs
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Scale Selector */}
                  <div>
                    <Label className="text-sm font-medium mb-3 block">Platform Scale (Total Users)</Label>
                    <div className="grid grid-cols-5 gap-2">
                      {Object.entries(scaleData).map(([key, data]) => (
                        <button
                          key={key}
                          onClick={() => setSelectedScale(key)}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            selectedScale === key 
                              ? 'border-primary bg-primary/5' 
                              : 'border-transparent bg-muted hover:bg-muted/80'
                          }`}
                        >
                          <div className={`w-3 h-3 rounded-full ${data.color} mx-auto mb-2`} />
                          <div className="font-bold text-sm">{data.users}</div>
                          <div className="text-xs text-muted-foreground">{data.label}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Cost Basis Toggle */}
                  <div>
                    <Label className="text-sm font-medium mb-3 block">Cost Basis</Label>
                    <div className="flex gap-2">
                      <Button
                        variant={costBasis === 'direct' ? 'default' : 'outline'}
                        onClick={() => setCostBasis('direct')}
                        className="flex-1"
                      >
                        <Server className="w-4 h-4 mr-2" />
                        Direct Infrastructure Only
                      </Button>
                      <Button
                        variant={costBasis === 'full' ? 'default' : 'outline'}
                        onClick={() => setCostBasis('full')}
                        className="flex-1"
                      >
                        <Users className="w-4 h-4 mr-2" />
                        Full Loaded (Infra + Labor)
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      {costBasis === 'direct' 
                        ? 'Servers, databases, CDN, APIs - excludes staff costs'
                        : 'All infrastructure plus proportional engineering, support, and operations labor'}
                    </p>
                  </div>

                  {/* Margin Slider */}
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <Label className="text-sm font-medium">Utility Margin</Label>
                      <span className="text-2xl font-bold text-primary">{utilityMargin}%</span>
                    </div>
                    <Slider
                      value={[utilityMargin]}
                      onValueChange={(v) => setUtilityMargin(v[0])}
                      min={8}
                      max={25}
                      step={1}
                      className="mb-2"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>8% (Minimal)</span>
                      <span className="text-green-600 font-medium">12-18% (Recommended)</span>
                      <span>25% (Maximum)</span>
                    </div>
                  </div>

                  {/* Results */}
                  <div className="grid md:grid-cols-3 gap-4 pt-4 border-t">
                    <Card className="bg-slate-100">
                      <CardContent className="pt-4">
                        <p className="text-sm text-muted-foreground">Base Platform Cost</p>
                        <p className="text-2xl font-bold">{formatCurrency(baseCost)}</p>
                        <p className="text-xs text-muted-foreground">/month ({costBasis === 'direct' ? 'infra only' : 'full loaded'})</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-blue-100 border-blue-300">
                      <CardContent className="pt-4">
                        <p className="text-sm text-blue-700">VBB Utility Fee</p>
                        <p className="text-2xl font-bold text-blue-800">{formatCurrency(utilityFee)}</p>
                        <p className="text-xs text-blue-600">/month (cost + {utilityMargin}%)</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-green-100 border-green-300">
                      <CardContent className="pt-4">
                        <p className="text-sm text-green-700">VBB Profit Margin</p>
                        <p className="text-2xl font-bold text-green-800">{formatCurrency(utilityFee - baseCost)}</p>
                        <p className="text-xs text-green-600">/month guaranteed</p>
                      </CardContent>
                    </Card>
                  </div>
                </CardContent>
              </Card>

              {/* Model Comparison */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Scale className="w-5 h-5" />
                    Business Model Comparison
                  </CardTitle>
                  <CardDescription>
                    Compare different fee structures for a club with ${customClubRevenue.toLocaleString()}/month revenue
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="mb-6">
                    <Label className="text-sm font-medium mb-2 block">Club Monthly Revenue</Label>
                    <div className="flex gap-2 items-center">
                      <span className="text-muted-foreground">$</span>
                      <Input
                        type="number"
                        value={customClubRevenue}
                        onChange={(e) => setCustomClubRevenue(Number(e.target.value))}
                        className="w-32"
                      />
                      <span className="text-muted-foreground">/month</span>
                      <div className="flex gap-1 ml-4">
                        {[5000, 10000, 25000, 50000].map(amount => (
                          <Button 
                            key={amount}
                            variant="outline" 
                            size="sm"
                            onClick={() => setCustomClubRevenue(amount)}
                          >
                            ${(amount/1000)}K
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    {modelComparison.map((model, idx) => (
                      <Card key={idx} className={`${idx === 1 ? 'border-2 border-blue-500 shadow-lg' : ''}`}>
                        {idx === 1 && <Badge className="absolute -top-2 left-4 bg-blue-500">Recommended</Badge>}
                        <CardHeader className="pb-2">
                          <div className={`w-3 h-3 rounded-full ${model.color} mb-2`} />
                          <CardTitle className="text-base">{model.model}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span className="text-sm text-muted-foreground">VBB Receives:</span>
                              <span className="font-bold">{formatCurrency(model.vbbShare)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-muted-foreground">Club Retains:</span>
                              <span className="font-bold text-green-600">{formatCurrency(model.clubRetains)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-muted-foreground">Effective Rate:</span>
                              <span className="font-medium">{((model.vbbShare / customClubRevenue) * 100).toFixed(1)}%</span>
                            </div>
                          </div>
                          
                          <div className="pt-3 border-t space-y-2">
                            <p className="text-xs font-medium text-green-700">Pros:</p>
                            <ul className="text-xs text-muted-foreground space-y-1">
                              {model.pros.map((pro, i) => (
                                <li key={i} className="flex items-start gap-1">
                                  <CheckCircle className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                                  {pro}
                                </li>
                              ))}
                            </ul>
                          </div>
                          
                          <div className="space-y-2">
                            <p className="text-xs font-medium text-red-700">Cons:</p>
                            <ul className="text-xs text-muted-foreground space-y-1">
                              {model.cons.map((con, i) => (
                                <li key={i} className="flex items-start gap-1">
                                  <AlertCircle className="w-3 h-3 text-red-400 mt-0.5 flex-shrink-0" />
                                  {con}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Utility Industry Benchmarks */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Info className="w-5 h-5" />
                    Industry Benchmarks: Regulated Utility Returns
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Industry</TableHead>
                        <TableHead>Typical ROE</TableHead>
                        <TableHead>Regulatory Body</TableHead>
                        <TableHead>Notes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-medium">Electric Utilities</TableCell>
                        <TableCell>9-11%</TableCell>
                        <TableCell>State PUCs</TableCell>
                        <TableCell className="text-sm text-muted-foreground">Stable, low-risk returns</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Natural Gas Distribution</TableCell>
                        <TableCell>9-10%</TableCell>
                        <TableCell>FERC / State PUCs</TableCell>
                        <TableCell className="text-sm text-muted-foreground">Similar to electric</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Water Utilities</TableCell>
                        <TableCell>9-12%</TableCell>
                        <TableCell>State PUCs</TableCell>
                        <TableCell className="text-sm text-muted-foreground">Capital-intensive</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Telecom (regulated)</TableCell>
                        <TableCell>10-14%</TableCell>
                        <TableCell>FCC</TableCell>
                        <TableCell className="text-sm text-muted-foreground">Higher due to tech investment</TableCell>
                      </TableRow>
                      <TableRow className="bg-blue-50">
                        <TableCell className="font-medium">VBB Platform (proposed)</TableCell>
                        <TableCell className="font-bold text-blue-600">12-18%</TableCell>
                        <TableCell>Club Agreement</TableCell>
                        <TableCell className="text-sm text-blue-700">Tech + service premium</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  <p className="text-sm text-muted-foreground mt-4">
                    <strong>Why 12-18%?</strong> Higher than traditional utilities because: (1) technology platforms require continuous R&D investment, 
                    (2) software has higher obsolescence risk, (3) customer service is more intensive than utility metering.
                  </p>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ==================== INFRASTRUCTURE COSTS TAB ==================== */}
            <TabsContent value="costs" className="space-y-6">
              {/* Scale Selector */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Platform Scale
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-5 gap-2">
                    {Object.entries(scaleData).map(([key, data]) => (
                      <button
                        key={key}
                        onClick={() => setSelectedScale(key)}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          selectedScale === key 
                            ? 'border-primary bg-primary/5' 
                            : 'border-transparent bg-muted hover:bg-muted/80'
                        }`}
                      >
                        <div className={`w-3 h-3 rounded-full ${data.color} mx-auto mb-2`} />
                        <div className="font-bold">{data.users}</div>
                        <div className="text-xs text-muted-foreground">{data.label}</div>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Cost Summary */}
              <div className="grid md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-blue-600 font-medium">Infrastructure</p>
                        <p className="text-3xl font-bold text-blue-700">{formatCurrency(current.infra.total)}</p>
                        <p className="text-xs text-blue-500">per month</p>
                      </div>
                      <Server className="w-10 h-10 text-blue-400" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-purple-600 font-medium">Labor & Staff</p>
                        <p className="text-3xl font-bold text-purple-700">{formatCurrency(current.labor.total)}</p>
                        <p className="text-xs text-purple-500">{current.staff} people</p>
                      </div>
                      <Headphones className="w-10 h-10 text-purple-400" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-amber-600 font-medium">Total Monthly</p>
                        <p className="text-3xl font-bold text-amber-700">{formatCurrency(current.infra.total + current.labor.total)}</p>
                        <p className="text-xs text-amber-500">operating cost</p>
                      </div>
                      <DollarSign className="w-10 h-10 text-amber-400" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-green-600 font-medium">Cost Per User</p>
                        <p className="text-3xl font-bold text-green-700">${current.perUser.toFixed(2)}</p>
                        <p className="text-xs text-green-500">per month</p>
                      </div>
                      <TrendingUp className="w-10 h-10 text-green-400" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Detailed Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle>Infrastructure Breakdown at {current.users} Users</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[
                      { label: 'Compute (Servers)', value: current.infra.compute, icon: Server, color: 'bg-blue-500' },
                      { label: 'Database (MongoDB)', value: current.infra.database, icon: HardDrive, color: 'bg-green-500' },
                      { label: 'File Storage', value: current.infra.storage, icon: HardDrive, color: 'bg-purple-500' },
                      { label: 'CDN & Bandwidth', value: current.infra.cdn, icon: Globe, color: 'bg-amber-500' },
                      { label: 'AI/LLM Services', value: current.infra.ai, icon: Bot, color: 'bg-pink-500' },
                      { label: 'Search (Elasticsearch)', value: current.infra.search, icon: Search, color: 'bg-cyan-500' },
                      { label: 'Other (Monitoring, etc.)', value: current.infra.other, icon: Shield, color: 'bg-gray-500' },
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg ${item.color} flex items-center justify-center`}>
                          <item.icon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between items-center mb-1">
                            <span className="font-medium">{item.label}</span>
                            <span className="font-bold">${item.value.toLocaleString()}/mo</span>
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className={`h-full ${item.color}`}
                              style={{ width: `${(item.value / current.infra.total) * 100}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Labor Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle>Labor Breakdown at {current.users} Users</CardTitle>
                </CardHeader>
                <CardContent>
                  {current.labor.total === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Heart className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>At this stage, the founder handles all operations</p>
                      <p className="text-sm">No additional labor costs</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {[
                        { label: 'User Support', value: current.labor.support, icon: Headphones, color: 'bg-blue-500' },
                        { label: 'Content Moderation', value: current.labor.moderation, icon: Eye, color: 'bg-red-500' },
                        { label: 'Community Management', value: current.labor.community, icon: Heart, color: 'bg-pink-500' },
                        { label: 'DevOps/Engineering', value: current.labor.devops, icon: Code, color: 'bg-green-500' },
                        { label: 'Trust & Safety', value: current.labor.trustSafety, icon: Shield, color: 'bg-purple-500' },
                      ].map((item, idx) => (
                        <div key={idx} className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg ${item.color} flex items-center justify-center`}>
                            <item.icon className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1">
                            <div className="flex justify-between items-center mb-1">
                              <span className="font-medium">{item.label}</span>
                              <span className="font-bold">${item.value.toLocaleString()}/mo</span>
                            </div>
                            <div className="h-2 bg-muted rounded-full overflow-hidden">
                              <div 
                                className={`h-full ${item.color}`}
                                style={{ width: `${(item.value / current.labor.total) * 100}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Scaling Roadmap */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="w-5 h-5" />
                    Infrastructure Scaling Roadmap
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {[
                      { scale: '0 - 100K', title: 'Foundation', cost: '$200/mo', items: ['Database indexes', 'Connection pooling', 'Basic monitoring', 'Single server'] },
                      { scale: '500K', title: 'Growth', cost: '$1,000/mo', items: ['Redis caching', 'CDN for assets', 'Rate limiting', 'Image compression'] },
                      { scale: '1M', title: 'Scale', cost: '$2,000/mo', items: ['Read replicas', 'Load balancer + 3 servers', 'Elasticsearch', 'S3 storage'] },
                      { scale: '10M', title: 'Enterprise', cost: '$16,000/mo', items: ['Database sharding', 'Multi-region', 'Akamai CDN', 'Microservices'] }
                    ].map((phase, idx) => (
                      <div key={idx} className="flex gap-4">
                        <div className="flex flex-col items-center">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${idx === 0 ? 'bg-green-500' : 'bg-muted'} text-white font-bold text-sm`}>
                            {idx === 0 ? <CheckCircle className="w-5 h-5" /> : idx + 1}
                          </div>
                          {idx < 3 && <div className="w-0.5 h-full bg-muted mt-2" />}
                        </div>
                        <div className="flex-1 pb-6">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline">{phase.scale} users</Badge>
                            <span className="font-bold">{phase.title}</span>
                            <span className="text-sm text-muted-foreground">({phase.cost} infra)</span>
                          </div>
                          <ul className="grid md:grid-cols-2 gap-1">
                            {phase.items.map((item, i) => (
                              <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                                <ArrowRight className="w-3 h-3" />
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ==================== AD REVENUE TAB ==================== */}
            <TabsContent value="revenue" className="space-y-6">
              {/* Key Metrics */}
              <div className="grid md:grid-cols-4 gap-4">
                <Card className="bg-emerald-900/10 border-emerald-700/30">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <DollarSign className="w-5 h-5 text-emerald-600" />
                      <span className="text-xs text-emerald-600">Current Phase</span>
                    </div>
                    <p className="text-2xl font-bold mt-2">Beta</p>
                    <p className="text-xs text-emerald-600">~156 users</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-blue-900/10 border-blue-700/30">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <Eye className="w-5 h-5 text-blue-600" />
                      <span className="text-xs text-blue-600">Est. CPM</span>
                    </div>
                    <p className="text-2xl font-bold mt-2">$2.50-$5.00</p>
                    <p className="text-xs text-blue-600">Pet niche premium</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-purple-900/10 border-purple-700/30">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <TrendingUp className="w-5 h-5 text-purple-600" />
                      <span className="text-xs text-purple-600">Target RPM</span>
                    </div>
                    <p className="text-2xl font-bold mt-2">$8-$18</p>
                    <p className="text-xs text-purple-600">Revenue per 1K views</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-amber-900/10 border-amber-700/30">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <Users className="w-5 h-5 text-amber-600" />
                      <span className="text-xs text-amber-600">Ad Eligible</span>
                    </div>
                    <p className="text-2xl font-bold mt-2">70%</p>
                    <p className="text-xs text-amber-600">Guest visitors only</p>
                  </CardContent>
                </Card>
              </div>

              {/* Revenue Projections Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calculator className="w-5 h-5" />
                    Ad Revenue Projections by Scale
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>MAU</TableHead>
                        <TableHead>Page Views/mo</TableHead>
                        <TableHead>Ad Impressions</TableHead>
                        <TableHead>CPM</TableHead>
                        <TableHead>Monthly Revenue</TableHead>
                        <TableHead>Annual Revenue</TableHead>
                        <TableHead>Phase</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {adRevenueProjections.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{row.mau.toLocaleString()}</TableCell>
                          <TableCell>{row.pageViews.toLocaleString()}</TableCell>
                          <TableCell>{row.adImpressions.toLocaleString()}</TableCell>
                          <TableCell>${row.cpm.toFixed(2)}</TableCell>
                          <TableCell className="font-medium text-green-600">${row.monthlyRevenue.toLocaleString()}</TableCell>
                          <TableCell className="font-medium text-green-600">${row.annualRevenue.toLocaleString()}</TableCell>
                          <TableCell>{getStatusBadge(row.status)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Assumptions */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    Assumptions & Notes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    {adAssumptions.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                        <span className="text-sm">{item.label}</span>
                        <span className="font-medium text-sm">{item.value}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="font-medium">Key Revenue Drivers</p>
                        <ul className="text-sm mt-2 space-y-1 text-muted-foreground">
                          <li>- Pet owner audience commands premium CPM rates ($3-8 vs $1-2 general)</li>
                          <li>- Guest-only ads preserve member value (ad-free experience for paid members)</li>
                          <li>- High engagement niche content increases page views per session</li>
                          <li>- SEO-friendly content attracts organic traffic</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                      <div>
                        <p className="font-medium">Important Caveats</p>
                        <ul className="text-sm mt-2 space-y-1 text-muted-foreground">
                          <li>- Actual revenue depends on ad fill rate and geographic distribution</li>
                          <li>- Q4 typically sees 20-40% higher CPMs (holiday advertising)</li>
                          <li>- Ad blockers may reduce effective impressions by 15-30%</li>
                          <li>- Premium placements (homepage hero) yield higher rates</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ==================== FOUNDING CLUB TAB ==================== */}
            <TabsContent value="founding" className="space-y-6">
              {/* Concept */}
              <Card className="border-amber-200 bg-amber-50/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-amber-800">
                    <Crown className="w-5 h-5" />
                    The Founding Club Advantage
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-amber-900">
                  <p className="mb-4">
                    <strong>AADC as the founding club</strong> bears the cost and risk of building this platform. 
                    Newer clubs created after the platform is successful will benefit from:
                  </p>
                  <ul className="space-y-2 mb-4">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <span>A proven, stable platform (no beta testing required)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <span>Established features and workflows (years of refinement)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <span>Network effects (cross-club discovery, shared resources)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <span>Lower risk (proven business model)</span>
                    </li>
                  </ul>
                  <p className="text-sm bg-amber-100 p-3 rounded-lg">
                    <strong>The Question:</strong> Should newer clubs contribute a small fee to AADC as recognition 
                    of the founding club's investment in building the platform? This is separate from VBB's service fees.
                  </p>
                </CardContent>
              </Card>

              {/* Founding Club Fee Calculator */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calculator className="w-5 h-5" />
                    Founding Club Remuneration Calculator
                  </CardTitle>
                  <CardDescription>
                    Model different scenarios for newer clubs contributing to AADC
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Newer Club Revenue Input */}
                  <div>
                    <Label className="text-sm font-medium mb-2 block">New Club Monthly Revenue</Label>
                    <div className="flex gap-2 items-center">
                      <span className="text-muted-foreground">$</span>
                      <Input
                        type="number"
                        value={customClubRevenue}
                        onChange={(e) => setCustomClubRevenue(Number(e.target.value))}
                        className="w-32"
                      />
                      <span className="text-muted-foreground">/month</span>
                    </div>
                  </div>

                  {/* Founding Club Fee Slider */}
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <Label className="text-sm font-medium">AADC Founding Club Fee</Label>
                      <span className="text-2xl font-bold text-amber-600">{foundingClubFee}%</span>
                    </div>
                    <Slider
                      value={[foundingClubFee]}
                      onValueChange={(v) => setFoundingClubFee(v[0])}
                      min={0}
                      max={5}
                      step={0.5}
                      className="mb-2"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>0% (No fee)</span>
                      <span className="text-amber-600 font-medium">1-2% (Recommended)</span>
                      <span>5% (Maximum)</span>
                    </div>
                  </div>

                  {/* Results */}
                  <div className="grid md:grid-cols-3 gap-4 pt-4 border-t">
                    <Card className="bg-amber-100 border-amber-300">
                      <CardContent className="pt-4">
                        <p className="text-sm text-amber-700">AADC Receives</p>
                        <p className="text-2xl font-bold text-amber-800">{formatCurrency(foundingClubRemuneration)}</p>
                        <p className="text-xs text-amber-600">/month per new club</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-blue-100 border-blue-300">
                      <CardContent className="pt-4">
                        <p className="text-sm text-blue-700">VBB Receives (Utility)</p>
                        <p className="text-2xl font-bold text-blue-800">{formatCurrency(customClubUtilityFee)}</p>
                        <p className="text-xs text-blue-600">/month (cost + {utilityMargin}%)</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-green-100 border-green-300">
                      <CardContent className="pt-4">
                        <p className="text-sm text-green-700">New Club Retains</p>
                        <p className="text-2xl font-bold text-green-800">
                          {formatCurrency(customClubRevenue - customClubUtilityFee - foundingClubRemuneration)}
                        </p>
                        <p className="text-xs text-green-600">/month net</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Fee Breakdown */}
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm font-medium mb-3">Complete Fee Breakdown for New Club</p>
                    <Table>
                      <TableBody>
                        <TableRow>
                          <TableCell>Club Gross Revenue</TableCell>
                          <TableCell className="text-right font-bold">{formatCurrency(customClubRevenue)}</TableCell>
                          <TableCell className="text-right">100%</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="text-blue-600">− VBB Platform Fee (Utility)</TableCell>
                          <TableCell className="text-right text-blue-600">({formatCurrency(customClubUtilityFee)})</TableCell>
                          <TableCell className="text-right text-blue-600">
                            {((customClubUtilityFee / customClubRevenue) * 100).toFixed(1)}%
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="text-amber-600">− AADC Founding Fee</TableCell>
                          <TableCell className="text-right text-amber-600">({formatCurrency(foundingClubRemuneration)})</TableCell>
                          <TableCell className="text-right text-amber-600">{foundingClubFee}%</TableCell>
                        </TableRow>
                        <TableRow className="border-t-2">
                          <TableCell className="font-bold text-green-600">= Club Net Retained</TableCell>
                          <TableCell className="text-right font-bold text-green-600">
                            {formatCurrency(customClubRevenue - customClubUtilityFee - foundingClubRemuneration)}
                          </TableCell>
                          <TableCell className="text-right font-bold text-green-600">
                            {(((customClubRevenue - customClubUtilityFee - foundingClubRemuneration) / customClubRevenue) * 100).toFixed(1)}%
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              {/* Governance Options */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Governance Options
                  </CardTitle>
                  <CardDescription>
                    "In the long run, this may be up to the clubs to work out"
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">Option A: Platform-Level Fee</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm space-y-2">
                        <p>VBB collects the founding club fee as part of the service agreement and remits to AADC.</p>
                        <ul className="text-muted-foreground space-y-1">
                          <li className="flex items-start gap-1">
                            <CheckCircle className="w-3 h-3 text-green-500 mt-1" />
                            Simple, automatic collection
                          </li>
                          <li className="flex items-start gap-1">
                            <CheckCircle className="w-3 h-3 text-green-500 mt-1" />
                            Transparent to all parties
                          </li>
                          <li className="flex items-start gap-1">
                            <AlertCircle className="w-3 h-3 text-amber-500 mt-1" />
                            May be seen as "double dipping"
                          </li>
                        </ul>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">Option B: Club-to-Club Agreement</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm space-y-2">
                        <p>New clubs negotiate directly with AADC, separate from VBB relationship.</p>
                        <ul className="text-muted-foreground space-y-1">
                          <li className="flex items-start gap-1">
                            <CheckCircle className="w-3 h-3 text-green-500 mt-1" />
                            Club autonomy preserved
                          </li>
                          <li className="flex items-start gap-1">
                            <CheckCircle className="w-3 h-3 text-green-500 mt-1" />
                            Relationship-based, flexible
                          </li>
                          <li className="flex items-start gap-1">
                            <AlertCircle className="w-3 h-3 text-amber-500 mt-1" />
                            More complex, may be ignored
                          </li>
                        </ul>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">Option C: Network Membership Fee</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm space-y-2">
                        <p>All clubs pay a small fee to a "Club Network Fund" managed by club representatives.</p>
                        <ul className="text-muted-foreground space-y-1">
                          <li className="flex items-start gap-1">
                            <CheckCircle className="w-3 h-3 text-green-500 mt-1" />
                            Democratic governance
                          </li>
                          <li className="flex items-start gap-1">
                            <CheckCircle className="w-3 h-3 text-green-500 mt-1" />
                            AADC recognized but not sole beneficiary
                          </li>
                          <li className="flex items-start gap-1">
                            <AlertCircle className="w-3 h-3 text-amber-500 mt-1" />
                            Requires governance infrastructure
                          </li>
                        </ul>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">Option D: Time-Limited Recognition</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm space-y-2">
                        <p>Founding club fee applies only to clubs joining within first 3-5 years of platform launch.</p>
                        <ul className="text-muted-foreground space-y-1">
                          <li className="flex items-start gap-1">
                            <CheckCircle className="w-3 h-3 text-green-500 mt-1" />
                            Recognizes early risk-taking
                          </li>
                          <li className="flex items-start gap-1">
                            <CheckCircle className="w-3 h-3 text-green-500 mt-1" />
                            Natural sunset provision
                          </li>
                          <li className="flex items-start gap-1">
                            <AlertCircle className="w-3 h-3 text-amber-500 mt-1" />
                            Creates "deadline" dynamics
                          </li>
                        </ul>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="mt-6 p-4 bg-slate-100 rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      <strong>Note:</strong> This is a governance question that the clubs themselves may need to decide. 
                      VBB can facilitate the framework, but the decision on whether (and how) newer clubs recognize 
                      the founding club's contributions should ultimately be a matter for the club community.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Projection Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Founding Club Revenue Projection
                  </CardTitle>
                  <CardDescription>
                    AADC income from newer clubs at different network sizes (at {foundingClubFee}% fee)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Network Size</TableHead>
                        <TableHead>Avg Club Revenue</TableHead>
                        <TableHead>AADC Monthly Income</TableHead>
                        <TableHead>AADC Annual Income</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {[
                        { clubs: 5, avgRev: 5000 },
                        { clubs: 10, avgRev: 8000 },
                        { clubs: 25, avgRev: 10000 },
                        { clubs: 50, avgRev: 12000 },
                        { clubs: 100, avgRev: 15000 }
                      ].map((row, idx) => {
                        const otherClubs = row.clubs - 1; // Excluding AADC itself
                        const monthlyIncome = otherClubs * row.avgRev * (foundingClubFee / 100);
                        return (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{row.clubs} clubs</TableCell>
                            <TableCell>${row.avgRev.toLocaleString()}/mo</TableCell>
                            <TableCell className="text-amber-600 font-medium">{formatCurrency(monthlyIncome)}</TableCell>
                            <TableCell className="text-amber-600 font-medium">{formatCurrency(monthlyIncome * 12)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  <p className="text-xs text-muted-foreground mt-4">
                    * Assumes all clubs except AADC pay the founding club fee. Actual adoption may vary.
                  </p>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Document Footer */}
      <section className="px-4 pb-16 no-print">
        <div className="container mx-auto max-w-6xl">
          <Card className="bg-slate-50">
            <CardContent className="pt-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Document Version: 1.0</p>
                  <p className="text-sm text-muted-foreground">Last Updated: {new Date().toLocaleDateString()}</p>
                  <p className="text-sm text-muted-foreground">Confidential - VBB Internal Use Only</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => window.print()}>
                    <Printer className="w-4 h-4 mr-2" />
                    Print
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => {
                      // Open print dialog with PDF save hint
                      const printInstructions = window.confirm(
                        'To save as PDF:\n\n' +
                        '1. Click OK to open print dialog\n' +
                        '2. Select "Save as PDF" as your printer\n' +
                        '3. Click Save\n\n' +
                        'Continue?'
                      );
                      if (printInstructions) window.print();
                    }}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Save as PDF
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Print Header - Only visible when printing */}
      <div className="hidden print:block print:mb-8">
        <h1 className="text-2xl font-bold">VBB Business Financials Review</h1>
        <p className="text-sm text-gray-600">Generated: {new Date().toLocaleString()}</p>
        <p className="text-sm text-gray-600">Confidential - Internal Use Only</p>
      </div>
    </div>
  );
};

export default BusinessFinancials;
